import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link as MuiLink,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Feedback as FeedbackIcon,
  OpenInNew as OpenInNewIcon,
  Assignment as AssignmentIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { COLORS } from '../config/colors';

interface Panel {
  id: string;
  name: string;
  email: string;
  role?: string;
  avatar?: string;
  availability?: string[];
}

interface Resume {
  id: string;
  candidateName: string;
  email?: string;
  jobId: string;
  jobTitle?: string;
  avatureFeedbackLink?: string;
}

interface InterviewSchedulingModalProps {
  open: boolean;
  onClose: () => void;
  resume: Resume | null;
  panels: Panel[];
  onSchedule: (resumeId: string, panelId: string, scheduleData: any) => Promise<void>;
  loading?: boolean;
}

export const InterviewSchedulingModal: React.FC<InterviewSchedulingModalProps> = ({
  open,
  onClose,
  resume,
  panels,
  onSchedule,
  loading = false,
}) => {
  const [selectedPanel, setSelectedPanel] = useState<Panel | null>(null);
  const [interviewDateTime, setInterviewDateTime] = useState<string>('');
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState('');
  const [avatureFeedbackLink, setAvatureFeedbackLink] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [copySuccess, setCopySuccess] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      resetForm();
    } else if (resume?.avatureFeedbackLink) {
      setAvatureFeedbackLink(resume.avatureFeedbackLink);
    }
  }, [open, resume]);

  const resetForm = () => {
    setSelectedPanel(null);
    setInterviewDateTime('');
    setDuration(60);
    setNotes('');
    setAvatureFeedbackLink('');
    setErrors({});
    setCopySuccess(false);
  };

  const isValidAvatureUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      const urlObj = new URL(url);
      return (
        urlObj.protocol === 'https:' &&
        (urlObj.hostname.includes('avature') || urlObj.hostname.includes('deloitte.com'))
      );
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedPanel) {
      newErrors.panel = 'Please select a panelist';
    }

    if (interviewDateTime) {
      const selectedDate = new Date(interviewDateTime);
      const now = new Date();

      // Add 1 hour buffer from current time
      const minAllowedTime = new Date(now.getTime() + 60 * 60 * 1000);

      if (selectedDate <= minAllowedTime) {
        newErrors.datetime = 'Interview must be scheduled at least 1 hour from now';
      }
    }

    if (avatureFeedbackLink && !isValidAvatureUrl(avatureFeedbackLink)) {
      newErrors.avatureLink =
        'Please enter a valid Avature feedback URL (must be HTTPS and contain avature or deloitte.com)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !resume || !selectedPanel) return;

    setSubmitting(true);
    try {
      const scheduleData = {
        panelId: selectedPanel.id,
        interviewDateTime: interviewDateTime || null,
        duration,
        notes,
        avatureFeedbackLink: avatureFeedbackLink.trim(),
        candidateName: resume.candidateName,
        candidateEmail: resume.email,
        jobId: resume.jobId,
        jobTitle: resume.jobTitle,
        panelistName: selectedPanel.name,
        panelistEmail: selectedPanel.email,
      };

      await onSchedule(resume.id, selectedPanel.id, scheduleData);
      onClose();
    } catch (error) {
      console.error('Error scheduling interview:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignPanelOnly = async () => {
    if (!selectedPanel || !resume) return;

    setSubmitting(true);
    try {
      const assignmentData = {
        panelOnly: true,
        avatureFeedbackLink: avatureFeedbackLink.trim(),
      };
      await onSchedule(resume.id, selectedPanel.id, assignmentData);
      onClose();
    } catch (error) {
      console.error('Error assigning panel:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyFeedbackLink = async () => {
    if (avatureFeedbackLink) {
      try {
        await navigator.clipboard.writeText(avatureFeedbackLink);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    }
  };

  const handlePreviewFeedbackLink = () => {
    if (avatureFeedbackLink && isValidAvatureUrl(avatureFeedbackLink)) {
      window.open(avatureFeedbackLink, '_blank', 'noopener,noreferrer');
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    // Add 1 hour buffer and round to next quarter hour
    const minTime = new Date(now.getTime() + 60 * 60 * 1000);
    const minutes = minTime.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;
    minTime.setMinutes(roundedMinutes, 0, 0);

    // Format for datetime-local input (YYYY-MM-DDTHH:mm)
    const year = minTime.getFullYear();
    const month = String(minTime.getMonth() + 1).padStart(2, '0');
    const day = String(minTime.getDate()).padStart(2, '0');
    const hours = String(minTime.getHours()).padStart(2, '0');
    const mins = String(minTime.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${mins}`;
  };

  const handleDateTimeChange = (value: string) => {
    setInterviewDateTime(value);
    if (value) {
      setErrors(prev => ({ ...prev, datetime: '' }));
    }
  };

  const handleAvatureLinkChange = (value: string) => {
    setAvatureFeedbackLink(value);
    if (errors.avatureLink) {
      setErrors(prev => ({ ...prev, avatureLink: '' }));
    }
    setCopySuccess(false);
  };

  const formatDisplayDateTime = (datetimeString: string) => {
    if (!datetimeString) return '';

    try {
      const date = new Date(datetimeString);

      // Check if date is valid
      if (isNaN(date.getTime())) return '';

      const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      };

      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return datetimeString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 38px 3px rgba(0,0,0,0.14)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'primary.main',
                color: 'white',
              }}
            >
              <ScheduleIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                {resume && (resume as any).assignedPanel ? 'Reassign Panel' : 'Assign Panel'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {resume && (resume as any).assignedPanel
                  ? `Update panel assignment for ${resume?.candidateName}`
                  : `Assign panelist and schedule interview for ${resume?.candidateName}`}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#6b7280' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* Candidate Info Card */}
        <Card sx={{ mb: 3, backgroundColor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
          <CardContent sx={{ py: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  {resume?.candidateName?.charAt(0)?.toUpperCase()}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {resume?.candidateName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {resume?.email}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={resume?.jobId}
                    size="small"
                    sx={{ backgroundColor: '#dbeafe', color: COLORS.PRIMARY_BLUE_DARK }}
                  />
                  <Chip label={resume?.jobTitle} size="small" variant="outlined" />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Current Assignment Display (if reassigning) */}
        {resume && (resume as any).assignedPanel && (
          <Alert
            severity="info"
            sx={{ mb: 3, borderRadius: 2 }}
            icon={<PersonIcon sx={{ color: COLORS.PRIMARY_BLUE_DARK }} />}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              Currently Assigned Panel:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24, backgroundColor: COLORS.PRIMARY_BLUE_DARK }}>
                {panels
                  .find(p => p.id === (resume as any).assignedPanel)
                  ?.name?.charAt(0)
                  ?.toUpperCase() || 'P'}
              </Avatar>
              <Typography variant="body2">
                <strong>
                  {panels.find(p => p.id === (resume as any).assignedPanel)?.name ||
                    (resume as any).assignedPanel}
                </strong>
                {panels.find(p => p.id === (resume as any).assignedPanel)?.email &&
                  ` (${panels.find(p => p.id === (resume as any).assignedPanel)?.email})`}
              </Typography>
            </Box>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Panel Selection */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
              1. Select Interview Panelist
            </Typography>
            <FormControl fullWidth error={!!errors.panel}>
              <InputLabel>Choose Panelist</InputLabel>
              <Select
                value={selectedPanel?.id || ''}
                onChange={e => {
                  const panel = panels.find(p => p.id === e.target.value);
                  setSelectedPanel(panel || null);
                  setErrors(prev => ({ ...prev, panel: '' }));
                }}
                label="Choose Panelist"
                sx={{ borderRadius: 2 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                      overflowY: 'auto',
                      maxWidth: 'calc(100vw - 64px)',
                      width: 'auto',
                      minWidth: '300px',
                    },
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  disablePortal: false,
                  container: document.body,
                }}
              >
                {panels.map(panel => (
                  <MenuItem
                    key={panel.id}
                    value={panel.id}
                    sx={{
                      maxWidth: 'calc(100vw - 80px)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1,
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: COLORS.PRIMARY_BLUE_DARK,
                          flexShrink: 0,
                        }}
                      >
                        {panel.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box
                        sx={{
                          minWidth: 0,
                          flex: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}
                        >
                          {panel.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            display: 'block',
                          }}
                        >
                          {panel.email} • {panel.role || 'Panelist'}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.panel && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.panel}
                </Typography>
              )}
            </FormControl>

            {selectedPanel && (
              <Card sx={{ mt: 2, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ backgroundColor: COLORS.PRIMARY_BLUE_DARK }}>
                      {selectedPanel.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedPanel.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedPanel.email} • {selectedPanel.role || 'Panelist'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Avature Feedback Link Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              2. Avature Feedback Link
            </Typography>

            <MuiLink
              href="https://deloitteus.avature.net/hiringmanagerusi?emailCampaignId=167"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                bgcolor: 'primary.50',
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                border: 1,
                borderColor: 'primary.200',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'primary.100',
                },
              }}
            >
              USI Recruitment Portal
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </MuiLink>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Interview Scheduling */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
              2. Schedule Interview Details
            </Typography>

            <Grid container spacing={2}>
              {/* Date and Time */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Interview Date & Time"
                  type="datetime-local"
                  value={interviewDateTime}
                  onChange={e => handleDateTimeChange(e.target.value)}
                  error={!!errors.datetime}
                  helperText={errors.datetime || 'Select the date and time for the interview'}
                  inputProps={{
                    min: getMinDateTime(),
                    step: 900, // 15 minute intervals
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                />
                {interviewDateTime && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{
                      mt: 0.5,
                      display: 'block',
                      fontWeight: 500,
                      bgcolor: 'primary.50',
                      p: 1,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'primary.200',
                    }}
                  >
                    📅 {formatDisplayDateTime(interviewDateTime)}
                  </Typography>
                )}
              </Grid>

              {/* Duration */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Duration"
                  value={duration}
                  onChange={e => setDuration(Number(e.target.value))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value={30}>30 minutes</MenuItem>
                  <MenuItem value={45}>45 minutes</MenuItem>
                  <MenuItem value={60}>1 hour</MenuItem>
                  <MenuItem value={90}>1.5 hours</MenuItem>
                  <MenuItem value={120}>2 hours</MenuItem>
                </TextField>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Additional Notes (Optional)"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Any special instructions or preparation notes for the interview..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Assignment Summary */}
        {selectedPanel && (
          <Alert
            severity="info"
            sx={{ mt: 3, borderRadius: 2 }}
            icon={<AssignmentIcon sx={{ color: COLORS.PRIMARY_BLUE_DARK }} />}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Assignment Summary:
            </Typography>
            <Typography variant="body2">
              <strong>{selectedPanel.name}</strong> will be assigned to{' '}
              <strong>{resume?.candidateName}</strong>
              {interviewDateTime && (
                <>
                  {' '}
                  with interview scheduled on{' '}
                  <strong>{formatDisplayDateTime(interviewDateTime)}</strong>
                </>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {interviewDateTime && `Duration: ${duration} minutes • `}
              {avatureFeedbackLink
                ? 'Avature feedback link configured'
                : 'No feedback link provided'}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'grey.200' }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>

        {selectedPanel && !interviewDateTime && (
          <Button
            variant="outlined"
            onClick={handleAssignPanelOnly}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <PersonIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              color: COLORS.PRIMARY_BLUE_DARK,
            }}
          >
            {submitting ? 'Assigning...' : 'Assign Panel Only'}
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedPanel || submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : <ScheduleIcon />}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
          }}
        >
          {submitting
            ? 'Processing...'
            : interviewDateTime
              ? 'Assign Panel & Schedule Interview'
              : 'Assign Panel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterviewSchedulingModal;
